import { isIconElement } from '../../../utils/isIconElement';
import clsx from 'clsx';
import React, { AnchorHTMLAttributes, forwardRef } from 'react';
import {
  Link as LinkAria,
  LinkProps as ReactAriaLinkProps,
} from 'react-aria-components';
import styles from './Link.module.scss';

const warnedAboutMissingIconPosition = new Set<string>();

export type LinkProps = Omit<
  ReactAriaLinkProps,
  'children' // Omitting because we only allow string children
> & {
  /** The content of the Link. */
  children: React.ReactNode;
  /** Indicates the element represents the current item within a set. */
  'aria-current'?: AnchorHTMLAttributes<HTMLAnchorElement>['aria-current'];
  /** The size, impacting its dimensions and typography. */
  size?: 'compact' | 'small' | 'medium' | 'large';
  /** The variant of the `Link` if it's styled as a button. */
  variant?:
    | 'brand'
    | 'danger'
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'ghost'
    | 'link';
  /** Specifies if the link has an icon, and on which side. */
  iconPosition?: 'start' | 'end';
  /** Renders the link as a square (button variants) or strips the underline decoration (link variant) when the content is icon-only. Use with `aria-label` or `aria-labelledby` to provide an accessible label. */
  iconOnly?: boolean;
  /** Renders the link in inverted colors for use on dark backgrounds. Only applies to the `link` variant. */
  inverted?: boolean;
  /**
   * Controls whether the noreferrer tag is added to the link.
   * Defaults to true.
   */
  noReferrer?: boolean;
  /** The ARIA role for the link element. Useful for navigation menus (e.g., "menuitem"). */
  role?: string;
};

// Tries to guess if a link's href is external or not.
// Currently, supports these domains (and any subdomains).
const DOMAIN_WHITELIST = ['zapier.com', 'zapier-staging.com', 'localhost'];

const isLinkExternal = (href?: string) => {
  if (!href || href.length === 0) {
    return false;
  }

  /*
   * This regex tests if an href needs to have a protocol prepended to it or not.
   * We must prepend the protocol for links that are missing it when needed (e.g. example.com),
   * otherwise URL's constructor defaults to using the baseUrl, reporting an incorrect `host`.
   * There are two cases where we know we *don't* need to add it, so we detect those here:
   *    1. starts with ?, /, #, or http
   *    2. a slash occurs before any periods (e.g. apps/foo, but not example.com/apps)
   */
  const matcher = /(^[?#/]|http)|^[^.]+(\/)/;
  let hrefWithProtocol = href;

  if (!matcher.test(href)) {
    hrefWithProtocol = `https://${href}`;
  }

  // default base url to zapier.com allowing `URL`'s constructor to parse relative/fragment links
  const baseUrl = 'https://zapier.com';
  // URL may blow up on invalid URLs
  let url;
  try {
    url = new URL(hrefWithProtocol, baseUrl);
  } catch (_) {
    return true; // well, if it's invalid it at least is not internal
  }
  const [topLevelDomain, secondLevelDomain] = url.host
    .split(':')[0]
    .split('.')
    .reverse();
  const domain = secondLevelDomain
    ? `${secondLevelDomain}.${topLevelDomain}`
    : topLevelDomain;
  return !DOMAIN_WHITELIST.includes(domain);
};

// Build rel attribute with security considerations for external links
const buildRel = (
  rel: string | undefined | null,
  isExternal: boolean,
  noReferrer: boolean
) => {
  // Links to other websites should default to security best practices for the `rel` attribute,
  // which fixes click-jacking vulnerabilities and other security/privacy issues.
  const externalLinkRel = [];

  if (isExternal) {
    externalLinkRel.push('noopener');
  }

  if (isExternal && noReferrer) {
    externalLinkRel.push('noreferrer');
  }

  const propsRel = rel?.split(/\s+/) ?? []; // merge together any `rel` values passed in with any `rel` values required for external links

  return [...new Set([...propsRel, ...externalLinkRel])].join(' ') || undefined;
};

/**
 * Use `Link` to render a link to another URL. Should always have a href. If `Link` doesn't link to a different URL, consider using a `Button` instead.
 *
 * To enable client-side routing, pass a `navigate` function to `DesignSystemProvider`.
 */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      children,
      className,
      href,
      isDisabled = false,
      size = 'medium',
      variant = 'link',
      iconPosition,
      iconOnly,
      inverted = false,
      rel,
      noReferrer = true,
      ...props
    },
    ref
  ) => {
    const trimmedHref = href ? href.trim() : href;
    const isExternal = isLinkExternal(trimmedHref);
    const finalRel = buildRel(rel, isExternal, noReferrer);

    // Extract data attributes from props
    const dataAttributes = Object.fromEntries(
      Object.entries(props).filter(([key]) => key.startsWith('data-'))
    );

    // Detect icon-only links: a single Icon or SVG child with no accompanying text.
    // The explicit `iconOnly` prop takes precedence; auto-detection is the fallback.
    const childrenArray = React.Children.toArray(children);
    const detectedIconOnly =
      childrenArray.length === 1 && isIconElement(childrenArray[0]);
    const effectiveIconOnly = iconOnly ?? detectedIconOnly;

    // Check for icons in children (only in development and when iconPosition is not provided)
    if (process.env.NODE_ENV !== 'production') {
      if (!effectiveIconOnly && !iconPosition) {
        const hasIconStart = isIconElement(childrenArray[0]);
        const hasIconEnd = isIconElement(childrenArray.at(-1));

        if (hasIconStart || hasIconEnd) {
          const warnKey = trimmedHref || 'no-href';
          if (!warnedAboutMissingIconPosition.has(warnKey)) {
            warnedAboutMissingIconPosition.add(warnKey);
            console.warn(
              'Link component detected an Icon, but `iconPosition` is missing.\n' +
                'Please pass `iconOnly={true}` (for icon-only links) or `iconPosition="start"`/`iconPosition="end"` (for links containing text and icons) to align with design system expectations.'
            );
          }
        }
      }
    }

    return (
      <LinkAria
        {...props}
        className={clsx(
          className,
          variant === 'link' && styles.link,
          variant !== 'link' && [styles.buttonBase, styles.button]
        )}
        href={trimmedHref}
        isDisabled={isDisabled}
        ref={ref}
        rel={finalRel}
        data-a11y-ignore={variant === 'brand' ? 'color-contrast' : undefined}
        data-size={size}
        data-variant={variant}
        data-inverted={inverted || undefined}
        data-has-icon-start={
          (!effectiveIconOnly && iconPosition === 'start') || undefined
        }
        data-has-icon-end={
          (!effectiveIconOnly && iconPosition === 'end') || undefined
        }
        data-icon-only={effectiveIconOnly || undefined}
        data-zds
        {...dataAttributes}
      >
        {children}
      </LinkAria>
    );
  }
);
